import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  BookOpen,
  Plus,
  Loader2,
  Trash2,
  Sparkles,
  Pencil,
  Save,
  X as XIcon,
  Upload,
  RefreshCw,
  Quote,
  CheckCircle2,
  AlertTriangle,
  Wand2,
  Tag as TagIcon,
  Image as ImageIcon,
  Link2,
  Type as TypeIcon,
  Star,
  LifeBuoy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import GlassCard from "@/components/GlassCard";
import GradientMesh from "@/components/GradientMesh";
import ConfirmDialog from "@/components/ConfirmDialog";
import ReportProblemDialog from "@/components/ReportProblemDialog";

// ------------------------------------------------------------------
// Brand Learning page
// ------------------------------------------------------------------
// Users teach Postmoro their brand voice by feeding it 3+ past posts.
// Examples can come from:
//   1) typed/pasted caption text
//   2) bulk-paste of multiple captions
//   3) uploaded screenshot OR downloaded post image (PNG/JPG/WEBP) + typed caption
//   4) public image URL + typed caption
//
// "Usable" examples = examples with caption text the LLM can actually study.
// Image-only examples count toward storage cap but NOT the analysis minimum.
// Server-side `Analyze` enforces the same rule and never charges an AI
// generation on failure.
// ------------------------------------------------------------------

const ACCEPTED_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const MAX_IMAGE_BYTES_RAW = 8 * 1024 * 1024; // 8 MB raw input; we compress before send
const MAX_COMPRESSED_BYTES = 1_400_000; // matches backend ~1.5 MB cap with headroom
const MAX_DIMENSION = 1280; // longest side after compression

/**
 * Compress an image File into a base64 JPEG/WEBP data URL whose total
 * length stays under MAX_COMPRESSED_BYTES. Mirrors Media Library logic.
 */
async function compressImageFile(file) {
  if (!file) return null;
  if (!ACCEPTED_IMAGE_TYPES.has(file.type)) {
    throw new Error("Only JPG, PNG, or WEBP files are supported.");
  }
  if (file.size > MAX_IMAGE_BYTES_RAW) {
    throw new Error("Image is larger than 8 MB. Please pick a smaller file.");
  }
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) {
    throw new Error("Could not read the image. Try a different file.");
  }
  const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, w, h);
  // Step quality down until under cap.
  let quality = 0.82;
  let dataUrl = canvas.toDataURL("image/jpeg", quality);
  while (dataUrl.length > MAX_COMPRESSED_BYTES && quality > 0.4) {
    quality -= 0.1;
    dataUrl = canvas.toDataURL("image/jpeg", quality);
  }
  if (dataUrl.length > MAX_COMPRESSED_BYTES) {
    throw new Error(
      "Image is still too large after compression. Try a smaller screenshot."
    );
  }
  return dataUrl;
}

function StatusPill({ tone = "neutral", children, testid }) {
  const palette =
    tone === "ok"
      ? "bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)] border-[color:rgba(27,168,159,0.35)]"
      : tone === "warn"
      ? "bg-[color:rgba(255,176,32,0.08)] text-[color:var(--warning)] border-[color:rgba(255,176,32,0.30)]"
      : "bg-[color:var(--surface-2)] text-[color:var(--text-2)] border-[color:var(--border-subtle)]";
  return (
    <span
      data-testid={testid}
      className={
        "inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-full border " +
        palette
      }
    >
      {children}
    </span>
  );
}

export default function BrandLearningPage() {
  const [meta, setMeta] = useState(null);
  const [examples, setExamples] = useState([]);
  const [capacity, setCapacity] = useState(20);
  const [minForAnalysis, setMinForAnalysis] = useState(3);
  const [styleProfile, setStyleProfile] = useState(null);
  const [styleActive, setStyleActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  // Rotating progress message shown during AI analysis. Pure UI sugar —
  // mirrors the polished "AILoadingState" we use elsewhere.
  const ANALYZE_STEPS = [
    "Analyzing your brand voice…",
    "Reviewing caption style…",
    "Finding recurring themes…",
    "Building your saved voice…",
  ];
  const [analyzeStep, setAnalyzeStep] = useState(0);
  useEffect(() => {
    if (!analyzing) {
      setAnalyzeStep(0);
      return;
    }
    const id = setInterval(() => {
      setAnalyzeStep((s) => (s + 1) % ANALYZE_STEPS.length);
    }, 1700);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyzing]);
  const [showAddSingle, setShowAddSingle] = useState(false);
  // When set, AddSingleForm opens directly on this input mode (paste|upload|url).
  // Used by the dedicated "Upload screenshot" top-level button so users land
  // on the upload zone without having to click the tab switcher.
  const [addMode, setAddMode] = useState("paste");
  const [showBulkPaste, setShowBulkPaste] = useState(false);
  const [editingExample, setEditingExample] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileDraft, setProfileDraft] = useState(null);
  const [analyzeError, setAnalyzeError] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportContext, setReportContext] = useState({});

  // Derived: usable examples (those with caption text the LLM can study).
  const usableCount = useMemo(
    () =>
      examples.reduce(
        (n, ex) =>
          n + ((ex.text || "").trim().length >= 4 ? 1 : 0),
        0
      ),
    [examples]
  );

  const remaining = useMemo(
    () => Math.max(0, capacity - examples.length),
    [capacity, examples.length]
  );

  const canAnalyze = usableCount >= minForAnalysis;

  const load = async () => {
    setLoading(true);
    try {
      const [m, ex, sp] = await Promise.all([
        api.get("/brand-learning/meta").catch(() => ({ data: null })),
        api.get("/brand-learning/examples").catch(() => ({ data: null })),
        api.get("/brand-learning/style-profile").catch(() => ({ data: null })),
      ]);
      if (m.data) {
        setMeta(m.data);
        setCapacity(m.data.max_examples || 20);
        setMinForAnalysis(m.data.min_for_analysis || 3);
      }
      if (ex.data) {
        setExamples(ex.data.items || []);
        if (typeof ex.data.capacity === "number") setCapacity(ex.data.capacity);
        if (typeof ex.data.min_for_analysis === "number")
          setMinForAnalysis(ex.data.min_for_analysis);
      }
      if (sp.data) {
        setStyleProfile(sp.data.profile || null);
        setStyleActive(!!sp.data.active);
      }
    } catch (_e) {
      toast.error("Couldn't load brand learning data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // ----------------- Add sample template -----------------
  // Loads a single, realistic-feeling example so a first-time user can SEE
  // what a complete usable example looks like before writing their own.
  // Backend still treats it as a normal example. Users can delete it.
  const SAMPLE_TEMPLATE = {
    text:
      "Friday vibes hit different ☕ Come grab a cold brew on the patio before the weekend rush. Bring a friend, stay a while. Open till 9pm tonight!",
    platform: "Instagram",
    notes: "Sample template — edit or delete and replace with your own posts.",
    tags: ["seasonal", "community"],
    is_high_performer: false,
  };
  const addSampleExample = async () => {
    await addExample(SAMPLE_TEMPLATE);
  };

  // ----------------- Add single -----------------
  const addExample = async (payload) => {
    setAdding(true);
    try {
      const res = await api.post("/brand-learning/examples", payload);
      setExamples((prev) => [res.data, ...prev]);
      toast.success("Example added");
      setShowAddSingle(false);
      return true;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't add example";
      toast.error(msg);
      return false;
    } finally {
      setAdding(false);
    }
  };

  // ----------------- Bulk paste -----------------
  const bulkAdd = async (text, platform) => {
    setAdding(true);
    try {
      const res = await api.post("/brand-learning/examples/bulk", {
        text,
        platform: platform || null,
      });
      const created = res.data.created || [];
      setExamples((prev) => [...created, ...prev].slice(0, capacity));
      if (res.data.skipped_count) {
        toast.warning(
          `Imported ${res.data.created_count} of ${
            res.data.created_count + res.data.skipped_count
          } (${capacity}-example cap reached).`
        );
      } else {
        toast.success(`Imported ${res.data.created_count} examples`);
      }
      setShowBulkPaste(false);
      return true;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't import examples";
      toast.error(msg);
      return false;
    } finally {
      setAdding(false);
    }
  };

  // ----------------- Edit -----------------
  const saveExampleEdit = async (id, patch) => {
    try {
      const res = await api.patch(`/brand-learning/examples/${id}`, patch);
      setExamples((prev) =>
        prev.map((x) => (x.example_id === id ? res.data : x))
      );
      toast.success("Saved");
      setEditingExample(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't save";
      toast.error(msg);
    }
  };

  // ----------------- Delete -----------------
  const deleteExample = async (id) => {
    try {
      await api.delete(`/brand-learning/examples/${id}`);
      setExamples((prev) => prev.filter((x) => x.example_id !== id));
      toast.success("Example deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete");
    } finally {
      setConfirmDeleteId(null);
    }
  };

  // ----------------- Analyze -----------------
  const runAnalyze = async () => {
    if (!canAnalyze) {
      toast.error(
        `Add at least ${minForAnalysis} examples with caption text to analyze your brand style.`
      );
      return;
    }
    setAnalyzing(true);
    setAnalyzeError(null);
    try {
      const res = await api.post("/brand-learning/analyze");
      setStyleProfile(res.data.profile || null);
      setStyleActive(true);
      toast.success("Brand style profile created");
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || "Brand analysis failed";
      if (status === 402 || /limit/i.test(String(msg))) {
        const friendly =
          "You've reached your monthly AI generation limit. Upgrade to analyze brand style.";
        setAnalyzeError({ message: friendly, isLimit: true });
        toast.error(friendly);
      } else if (status === 400) {
        // User-config issue (e.g. not enough usable text). Surface inline,
        // don't trigger the "Report this issue" affordance for these.
        setAnalyzeError({ message: msg, isLimit: false, isConfig: true });
        toast.error(msg);
      } else {
        const friendly =
          "Brand analysis failed. Your examples are saved — please try again.";
        setAnalyzeError({
          message: friendly,
          raw: msg,
          isLimit: false,
          canReport: true,
        });
        toast.error(friendly);
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const openReport = () => {
    setReportContext({
      subject: "Brand analysis failed",
      category: "ai",
      priority: "normal",
      message:
        "Brand analysis failed when I clicked Analyze Brand Style.\n\n" +
        (analyzeError?.raw ? `Server detail: ${analyzeError.raw}\n` : "") +
        `Usable examples at time of failure: ${usableCount} (cap ${capacity}).`,
    });
    setReportOpen(true);
  };

  // ----------------- Style profile edit -----------------
  const startEditProfile = () => {
    setProfileDraft({
      brand_voice: styleProfile?.brand_voice || "",
      tone: styleProfile?.tone || "",
      common_phrases: (styleProfile?.common_phrases || []).join(", "),
      emoji_style: styleProfile?.emoji_style || "",
      hashtag_style: styleProfile?.hashtag_style || "",
      cta_style: styleProfile?.cta_style || "",
      typical_length: styleProfile?.typical_length || "",
      common_topics: (styleProfile?.common_topics || []).join(", "),
      content_themes: (styleProfile?.content_themes || []).join(", "),
      audience_style: styleProfile?.audience_style || "",
      do_guidelines: (styleProfile?.do_guidelines || []).join("\n"),
      dont_guidelines: (styleProfile?.dont_guidelines || []).join("\n"),
    });
    setEditingProfile(true);
  };

  const saveProfileEdit = async () => {
    const splitCsv = (s) =>
      (s || "").split(/[,\n]/).map((x) => x.trim()).filter(Boolean);
    const splitLines = (s) =>
      (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
    try {
      const res = await api.put("/brand-learning/style-profile", {
        brand_voice: profileDraft.brand_voice,
        tone: profileDraft.tone,
        common_phrases: splitCsv(profileDraft.common_phrases),
        emoji_style: profileDraft.emoji_style,
        hashtag_style: profileDraft.hashtag_style,
        cta_style: profileDraft.cta_style,
        typical_length: profileDraft.typical_length,
        common_topics: splitCsv(profileDraft.common_topics),
        content_themes: splitCsv(profileDraft.content_themes),
        audience_style: profileDraft.audience_style,
        do_guidelines: splitLines(profileDraft.do_guidelines),
        dont_guidelines: splitLines(profileDraft.dont_guidelines),
      });
      setStyleProfile(res.data);
      setStyleActive(true);
      setEditingProfile(false);
      toast.success("Style profile saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save style profile");
    }
  };

  const deactivateProfile = async () => {
    try {
      await api.delete("/brand-learning/style-profile");
      setStyleActive(false);
      toast.success("Style profile deactivated. AI will use your defaults.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't deactivate");
    }
  };

  return (
    <div className="relative min-h-screen" data-testid="brand-learning-page">
      <GradientMesh />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <PageHeader
          eyebrow="Brand voice"
          title="Brand Learning"
          subtitle="Let Postmoro learn from your past posts so future content sounds more like your business. Already have an established social presence? Add examples of your existing posts so Postmoro can better match your voice and style."
          icon={BookOpen}
        />

        {/* Brand Voice Hero — real state + real usable-example progress.
            Every number below comes from the existing backend response;
            no invented confidence scores, no invented readiness %.  */}
        <GlassCard className="p-5 mt-5 relative overflow-hidden" data-testid="brand-learning-hero">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-20 -right-20 h-56 w-56 rounded-full"
            style={{ background: "radial-gradient(closest-side, rgba(27,168,159,0.22), transparent 70%)" }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-20 -left-16 h-52 w-52 rounded-full"
            style={{ background: "radial-gradient(closest-side, rgba(34,211,238,0.14), transparent 70%)" }}
          />
          <div className="relative flex items-start gap-4 flex-wrap">
            {/* Left: heading + real state */}
            <div className="flex-1 min-w-[240px]">
              <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--brand-primary)]">Brand voice status</div>
              <div className="font-display text-2xl sm:text-3xl font-semibold mt-1" data-testid="brand-hero-state-label">
                {styleActive && styleProfile
                  ? "Your saved voice is guiding new posts"
                  : canAnalyze
                  ? "Ready to learn your voice"
                  : (examples.length > 0 || usableCount > 0)
                  ? "Add a few more examples"
                  : "Teach Postmoro your voice"}
              </div>
              <div className="text-xs text-[color:var(--text-3)] mt-1.5 max-w-lg leading-relaxed">
                {styleActive && styleProfile
                  ? "Postmoro will match this saved voice on every new caption. You can pause or re-analyze anytime."
                  : canAnalyze
                  ? "You have enough usable examples. Run brand analysis to build your saved voice."
                  : "Paste captions, upload post screenshots, or add image URLs. We only study what you add."}
              </div>

              {/* Real 4-state pill row */}
              <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]">
                {styleActive && styleProfile ? (
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full border border-[color:rgba(27,168,159,0.45)] bg-[color:rgba(27,168,159,0.12)] text-[color:var(--brand-primary)] px-2 py-0.5"
                    data-testid="brand-hero-state-active"
                  >
                    <CheckCircle2 className="h-3 w-3" /> Active
                  </span>
                ) : canAnalyze ? (
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full border border-[color:rgba(255,190,110,0.45)] bg-[color:rgba(255,190,110,0.10)] text-[color:var(--accent-warm)] px-2 py-0.5"
                    data-testid="brand-hero-state-ready"
                  >
                    <Wand2 className="h-3 w-3" /> Ready to analyze
                  </span>
                ) : (examples.length > 0 || usableCount > 0) ? (
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full border border-[color:rgba(255,190,110,0.45)] bg-[color:rgba(255,190,110,0.10)] text-[color:var(--accent-warm)] px-2 py-0.5"
                    data-testid="brand-hero-state-in-progress"
                  >
                    <AlertTriangle className="h-3 w-3" /> In progress
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-2)] px-2 py-0.5"
                    data-testid="brand-hero-state-not-started"
                  >
                    Not started
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-2)] px-2 py-0.5">
                  {examples.length} / {capacity} examples
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-2)] px-2 py-0.5">
                  {usableCount} usable for analysis
                </span>
              </div>
            </div>

            {/* Right: real progress toward the minimum-for-analysis threshold */}
            <div
              className="min-w-[220px] sm:w-[260px] rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3"
              data-testid="brand-hero-progress"
            >
              <div className="flex items-center justify-between text-[11px] mb-1.5">
                <span className="uppercase tracking-[0.14em] text-[color:var(--text-3)]">Usable / minimum</span>
                <span className="font-mono-tabular text-[color:var(--text-1)]">
                  {Math.min(usableCount, minForAnalysis)} / {minForAnalysis}
                </span>
              </div>
              <div className="h-2 rounded-full bg-[color:var(--surface-3)] overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[color:var(--brand-primary)] to-[color:var(--brand-secondary)] transition-all"
                  style={{
                    width:
                      Math.max(
                        0,
                        Math.min(100, (usableCount / Math.max(1, minForAnalysis)) * 100)
                      ) + "%",
                  }}
                />
              </div>
              <div className="text-[10px] text-[color:var(--text-3)] mt-1.5 leading-snug">
                {canAnalyze
                  ? "You have enough usable examples to run brand analysis."
                  : `Add ${Math.max(0, minForAnalysis - usableCount)} more usable example${
                      Math.max(0, minForAnalysis - usableCount) === 1 ? "" : "s"
                    } with caption text to unlock analysis.`}
              </div>
            </div>
          </div>
        </GlassCard>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6">
          {/* LEFT: examples */}
          <GlassCard className="p-6 lg:col-span-7" data-testid="brand-examples-card">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[color:var(--border-subtle)]">
              <div className="h-8 w-8 rounded-[10px] bg-gradient-to-br from-[color:rgba(27,168,159,0.24)] to-[color:rgba(34,211,238,0.16)] flex items-center justify-center border border-[color:rgba(27,168,159,0.35)]">
                <Quote className="h-4 w-4 text-[color:var(--brand-primary)]" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--brand-primary)]">Past Posts</div>
                <div className="text-xs text-[color:var(--text-2)] leading-tight mt-0.5">Examples Postmoro will study. Add posts you own or have permission to use.</div>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <StatusPill testid="brand-examples-count">
                  {examples.length} / {capacity} examples
                </StatusPill>
                <StatusPill
                  tone={usableCount >= minForAnalysis ? "ok" : "warn"}
                  testid="brand-examples-usable-count"
                >
                  {usableCount} usable for analysis
                </StatusPill>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              <Button
                size="sm"
                onClick={() => { setAddMode("paste"); setShowAddSingle(true); }}
                disabled={remaining === 0}
                data-testid="brand-add-text-btn"
                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)]"
              >
                <Plus className="h-4 w-4 mr-1.5" /> Add text example
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setAddMode("upload"); setShowAddSingle(true); }}
                disabled={remaining === 0}
                data-testid="brand-upload-screenshot-btn"
                className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]"
              >
                <ImageIcon className="h-4 w-4 mr-1.5" /> Upload screenshot/post image
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowBulkPaste(true)}
                disabled={remaining === 0}
                data-testid="brand-bulk-paste-btn"
                className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]"
              >
                <Upload className="h-4 w-4 mr-1.5" /> Paste multiple
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setAddMode("url"); setShowAddSingle(true); }}
                disabled={remaining === 0}
                data-testid="brand-add-url-btn"
                className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]"
              >
                <Link2 className="h-4 w-4 mr-1.5" /> Add image URL
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={load}
                disabled={loading}
                data-testid="brand-refresh-btn"
                className="text-[color:var(--text-3)] hover:text-[color:var(--text-1)]"
              >
                <RefreshCw className={"h-4 w-4 mr-1.5 " + (loading ? "animate-spin" : "")} />
                Refresh
              </Button>
            </div>

            {loading ? (
              <div className="text-sm text-[color:var(--text-3)] flex items-center gap-2 py-12 justify-center">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading examples…
              </div>
            ) : examples.length === 0 ? (
              <div className="text-center py-12" data-testid="brand-examples-empty">
                <div className="mx-auto w-12 h-12 rounded-full bg-[color:var(--surface-2)] flex items-center justify-center mb-3">
                  <Quote className="h-5 w-5 text-[color:var(--text-3)]" />
                </div>
                <div className="text-sm text-[color:var(--text-2)] font-medium">
                  No examples yet
                </div>
                <div className="text-xs text-[color:var(--text-3)] mt-1">
                  Add at least {minForAnalysis} past posts with caption text to run brand analysis.
                </div>
              </div>
            ) : (
              <ul className="space-y-3" data-testid="brand-examples-list">
                {examples.map((ex) => (
                  <li
                    key={ex.example_id}
                    className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3"
                    data-testid={`brand-example-${ex.example_id}`}
                  >
                    {editingExample === ex.example_id ? (
                      <ExampleEditor
                        initial={ex}
                        meta={meta}
                        onCancel={() => setEditingExample(null)}
                        onSave={(patch) => saveExampleEdit(ex.example_id, patch)}
                      />
                    ) : (
                      <ExampleRow
                        example={ex}
                        onEdit={() => setEditingExample(ex.example_id)}
                        onDelete={() => setConfirmDeleteId(ex.example_id)}
                      />
                    )}
                  </li>
                ))}
              </ul>
            )}
          </GlassCard>

          {/* RIGHT: analyze + style profile */}
          <div className="lg:col-span-5 space-y-6">
            <GlassCard className="p-6" data-testid="brand-analyze-card">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[color:var(--border-subtle)]">
                <div className="h-8 w-8 rounded-[10px] bg-gradient-to-br from-[color:rgba(34,211,238,0.24)] to-[color:rgba(27,168,159,0.16)] flex items-center justify-center border border-[color:rgba(34,211,238,0.35)]">
                  <Wand2 className="h-4 w-4 text-[color:var(--brand-secondary)]" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--brand-secondary)]">Step 2 · Analyze</div>
                  <div className="text-xs text-[color:var(--text-2)] leading-tight mt-0.5">Build your saved brand voice from the examples above.</div>
                </div>
              </div>
              <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                  <div className="font-display text-lg font-semibold">
                    Analyze brand style
                  </div>
                  <div className="text-xs text-[color:var(--text-3)] mt-1">
                    Postmoro uses these examples only to learn your business
                    voice and improve your generated content.
                  </div>
                </div>
                {(() => {
                  // 4 canonical states (same vocabulary as BrandLearningStatusCard)
                  if (styleActive && styleProfile) {
                    return (
                      <StatusPill tone="ok" testid="brand-status-active">
                        <CheckCircle2 className="h-3 w-3" /> Active
                      </StatusPill>
                    );
                  }
                  if (canAnalyze) {
                    return (
                      <StatusPill tone="warn" testid="brand-status-ready">
                        <Wand2 className="h-3 w-3" /> Ready to analyze
                      </StatusPill>
                    );
                  }
                  if (examples.length > 0 || usableCount > 0) {
                    return (
                      <StatusPill tone="warn" testid="brand-status-in-progress">
                        <AlertTriangle className="h-3 w-3" /> In progress
                      </StatusPill>
                    );
                  }
                  return (
                    <StatusPill testid="brand-status-not-started">Not started</StatusPill>
                  );
                })()}
              </div>
              {(() => {
                // Helper message under status, matches state.
                let msg = null;
                if (styleActive && styleProfile) {
                  msg = "Brand Style Profile is active and will guide future content.";
                } else if (canAnalyze) {
                  msg = "You have enough examples. Run brand analysis to create your saved voice.";
                } else if (examples.length > 0 || usableCount > 0) {
                  msg = `Add at least ${minForAnalysis} examples to analyze your brand style.${
                    usableCount > 0 && usableCount < minForAnalysis
                      ? ` (${usableCount} of ${minForAnalysis} ready.)`
                      : ""
                  }`;
                } else {
                  msg = "Add examples to teach Postmoro your style.";
                }
                return (
                  <div className="text-xs text-[color:var(--text-3)] mb-3" data-testid="brand-status-message">
                    {msg}
                  </div>
                );
              })()}
              <div className="text-xs text-[color:var(--text-3)] mb-3">
                {canAnalyze && !styleActive
                  ? <>Ready to analyze. This will count as <strong>1 AI generation</strong>.</>
                  : <>Running brand analysis counts as <strong>1 AI generation</strong>. Failed analysis does not count.</>
                }
              </div>
              <Button
                onClick={runAnalyze}
                disabled={!canAnalyze || analyzing}
                data-testid="brand-analyze-btn"
                className="w-full bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)]"
              >
                {analyzing ? (
                  <span className="inline-flex items-center gap-2" data-testid="brand-analyze-loading">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{ANALYZE_STEPS[analyzeStep]}</span>
                  </span>
                ) : styleProfile ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" /> Re-analyze brand style
                  </>
                ) : (
                  <>
                    <Wand2 className="h-4 w-4 mr-2" /> Analyze brand style
                  </>
                )}
              </Button>
              {!canAnalyze ? (
                <div
                  className="mt-3 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3"
                  data-testid="brand-analyze-not-ready"
                >
                  <div className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)] mb-2">
                    Get ready
                  </div>
                  <ul className="space-y-1.5 text-xs">
                    <li className="flex items-center gap-2" data-testid="brand-checklist-1">
                      <span className={
                        "h-4 w-4 rounded-full border flex items-center justify-center shrink-0 " +
                        (examples.length >= 1
                          ? "bg-[color:rgba(27,168,159,0.18)] border-[color:var(--brand-primary)]"
                          : "border-[color:var(--border-subtle)]")
                      }>
                        {examples.length >= 1 ? <CheckCircle2 className="h-3 w-3 text-[color:var(--brand-primary)]" /> : null}
                      </span>
                      <span className={examples.length >= 1 ? "text-[color:var(--text-2)] line-through" : "text-[color:var(--text-1)]"}>
                        Add at least one example
                      </span>
                    </li>
                    <li className="flex items-center gap-2" data-testid="brand-checklist-2">
                      <span className={
                        "h-4 w-4 rounded-full border flex items-center justify-center shrink-0 " +
                        (usableCount >= minForAnalysis
                          ? "bg-[color:rgba(27,168,159,0.18)] border-[color:var(--brand-primary)]"
                          : "border-[color:var(--border-subtle)]")
                      }>
                        {usableCount >= minForAnalysis ? <CheckCircle2 className="h-3 w-3 text-[color:var(--brand-primary)]" /> : null}
                      </span>
                      <span className={usableCount >= minForAnalysis ? "text-[color:var(--text-2)] line-through" : "text-[color:var(--text-1)]"}>
                        Make sure each example has caption text ({usableCount} of {minForAnalysis} ready)
                      </span>
                    </li>
                    <li className="flex items-center gap-2" data-testid="brand-checklist-3">
                      <span className="h-4 w-4 rounded-full border border-[color:var(--border-subtle)] shrink-0" />
                      <span className="text-[color:var(--text-1)]">
                        Click Analyze Brand Style
                      </span>
                    </li>
                  </ul>
                  {examples.length === 0 ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={addSampleExample}
                      disabled={adding || remaining === 0}
                      data-testid="brand-add-sample-btn"
                      className="mt-3 w-full border-[color:var(--border-subtle)] bg-[color:var(--surface-3)] hover:bg-[color:var(--surface-2)]"
                    >
                      <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Use example template
                    </Button>
                  ) : null}
                </div>
              ) : null}
              {analyzeError ? (
                <div
                  className="mt-3 rounded-md border border-[color:rgba(238,90,82,0.30)] bg-[color:rgba(238,90,82,0.06)] p-3 text-xs"
                  data-testid="brand-analyze-error"
                >
                  <div className="text-[color:var(--danger)] font-medium mb-1 flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {analyzeError.isLimit ? "AI limit reached" : "Analysis didn't finish"}
                  </div>
                  <div className="text-[color:var(--text-2)]">{analyzeError.message}</div>
                  {analyzeError.canReport ? (
                    <button
                      type="button"
                      onClick={openReport}
                      data-testid="brand-analyze-report-btn"
                      className="mt-2 inline-flex items-center gap-1 text-[11px] text-[color:var(--brand-primary)] hover:underline"
                    >
                      <LifeBuoy className="h-3 w-3" /> Report this issue
                    </button>
                  ) : null}
                </div>
              ) : null}
            </GlassCard>

            <GlassCard className="p-6" data-testid="brand-style-profile-card">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[color:var(--border-subtle)]">
                <div className="h-8 w-8 rounded-[10px] bg-gradient-to-br from-[color:rgba(27,168,159,0.24)] to-[color:rgba(34,211,238,0.16)] flex items-center justify-center border border-[color:rgba(27,168,159,0.35)]">
                  <Sparkles className="h-4 w-4 text-[color:var(--brand-primary)]" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--brand-primary)]">Brand Style Profile</div>
                  <div className="text-xs text-[color:var(--text-2)] leading-tight mt-0.5">The saved voice Postmoro applies to every new caption.</div>
                </div>
              </div>
              <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                  <div className="font-display text-lg font-semibold">Your saved voice</div>
                </div>
                {styleProfile ? (
                  editingProfile ? (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingProfile(false)}
                        data-testid="brand-style-cancel-btn"
                        className="h-8 px-3"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={saveProfileEdit}
                        data-testid="brand-style-save-btn"
                        className="h-8 px-3 bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)]"
                      >
                        <Save className="h-3.5 w-3.5 mr-1.5" /> Save
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={startEditProfile}
                        data-testid="brand-style-edit-btn"
                        className="h-8 px-3 text-[color:var(--text-3)] hover:text-[color:var(--text-1)]"
                      >
                        <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                      </Button>
                      {styleActive ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={deactivateProfile}
                          data-testid="brand-style-deactivate-btn"
                          className="h-8 px-3 text-[color:var(--text-3)] hover:text-[color:var(--danger)]"
                        >
                          Pause
                        </Button>
                      ) : null}
                    </div>
                  )
                ) : null}
              </div>
              {!styleProfile ? (
                <div className="text-center py-8 text-sm text-[color:var(--text-3)]" data-testid="brand-style-empty">
                  <Sparkles className="h-5 w-5 text-[color:var(--text-3)] mx-auto mb-2" />
                  No style profile yet.<br />
                  Add examples and run analysis to generate one.
                </div>
              ) : editingProfile ? (
                <StyleProfileEditor draft={profileDraft} onChange={setProfileDraft} />
              ) : (
                <StyleProfileView profile={styleProfile} />
              )}
            </GlassCard>

            <SocialImportCard />
          </div>
        </div>
      </div>

      <Dialog open={showAddSingle} onOpenChange={setShowAddSingle}>
        <DialogContent
          className="bg-[color:var(--surface-1)] border-[color:var(--border-subtle)] max-w-lg max-h-[90vh] overflow-y-auto"
          data-testid="brand-add-dialog"
        >
          <DialogHeader>
            <DialogTitle>Add a past post example</DialogTitle>
          </DialogHeader>
          <AddSingleForm
            meta={meta}
            saving={adding}
            initialMode={addMode}
            onSubmit={addExample}
            onCancel={() => setShowAddSingle(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={showBulkPaste} onOpenChange={setShowBulkPaste}>
        <DialogContent
          className="bg-[color:var(--surface-1)] border-[color:var(--border-subtle)] max-w-lg"
          data-testid="brand-bulk-dialog"
        >
          <DialogHeader>
            <DialogTitle>Paste multiple past posts</DialogTitle>
          </DialogHeader>
          <BulkPasteForm
            meta={meta}
            saving={adding}
            onSubmit={bulkAdd}
            onCancel={() => setShowBulkPaste(false)}
            remainingCapacity={remaining}
          />
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDeleteId}
        onOpenChange={(v) => !v && setConfirmDeleteId(null)}
        title="Delete this example?"
        description="It will be removed from the examples Postmoro learns from. You can re-add it later."
        confirmLabel="Delete"
        destructive
        onConfirm={() => confirmDeleteId && deleteExample(confirmDeleteId)}
      />

      <ReportProblemDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        context={reportContext}
      />
    </div>
  );
}

// ------------------------------------------------------------------
// Example row (read-only)
// ------------------------------------------------------------------
function ExampleRow({ example: ex, onEdit, onDelete }) {
  const hasText = (ex.text || "").trim().length >= 4;
  const hasImage = !!ex.image_url;
  return (
    <>
      {/* Image-on-top when present — feels closer to a real social post card */}
      {hasImage ? (
        <a
          href={ex.image_url}
          target="_blank"
          rel="noreferrer"
          data-testid={`brand-example-thumb-${ex.example_id}`}
          className="block rounded-md overflow-hidden border border-[color:var(--border-subtle)] bg-[color:var(--surface-3)] mb-3 max-h-72"
        >
          <img
            src={ex.image_url}
            alt=""
            loading="lazy"
            className="w-full h-full max-h-72 object-contain"
          />
        </a>
      ) : null}

      <div className="flex items-center flex-wrap gap-2 mb-1.5 text-[11px]">
        {ex.platform ? (
          <Badge
            variant="outline"
            className="text-[10px] py-0 border-[color:var(--border-subtle)] bg-[color:var(--surface-3)] text-[color:var(--text-2)]"
          >
            {ex.platform}
          </Badge>
        ) : null}
        {ex.date ? (
          <span className="text-[color:var(--text-3)]">{ex.date}</span>
        ) : null}
        {ex.is_high_performer ? (
          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[color:rgba(255,176,32,0.12)] border border-[color:rgba(255,176,32,0.35)] text-[color:var(--warning)]">
            <Star className="h-3 w-3" /> Performed well
          </span>
        ) : null}
        {hasText ? (
          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[color:rgba(27,168,159,0.10)] border border-[color:rgba(27,168,159,0.35)] text-[color:var(--brand-primary)]">
            <CheckCircle2 className="h-3 w-3" /> Usable for analysis
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[color:rgba(255,176,32,0.10)] border border-[color:rgba(255,176,32,0.30)] text-[color:var(--warning)]">
            <AlertTriangle className="h-3 w-3" /> Add caption text
          </span>
        )}
        {(ex.tags || []).map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 text-[color:var(--brand-primary)] text-[10px]"
          >
            <TagIcon className="h-3 w-3" /> {t}
          </span>
        ))}
      </div>

      {hasText ? (
        <div className="text-sm text-[color:var(--text-1)] whitespace-pre-wrap break-words">
          {ex.text}
        </div>
      ) : hasImage ? (
        <div className="rounded-md border border-[color:rgba(255,176,32,0.30)] bg-[color:rgba(255,176,32,0.05)] p-3 space-y-2">
          <div className="text-xs text-[color:var(--warning)] flex items-center gap-1.5 font-medium">
            <AlertTriangle className="h-3.5 w-3.5" /> Needs caption text
          </div>
          <div className="text-[11px] text-[color:var(--text-2)] leading-snug">
            Postmoro can study the writing style from the caption.
            Add the caption text from this screenshot so this example
            counts toward analysis.
          </div>
          <Button
            size="sm"
            onClick={onEdit}
            data-testid={`brand-example-add-caption-${ex.example_id}`}
            className="h-7 bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)]"
          >
            <Pencil className="h-3.5 w-3.5 mr-1.5" /> Add caption text
          </Button>
        </div>
      ) : (
        <div className="text-xs text-[color:var(--text-3)] italic">
          No content yet.
        </div>
      )}

      {ex.notes ? (
        <div className="mt-2 text-[11px] text-[color:var(--text-3)] italic">
          “{ex.notes}”
        </div>
      ) : null}

      <div className="mt-3 flex items-center gap-2">
        <Button
          size="sm"
          variant="ghost"
          onClick={onEdit}
          data-testid={`brand-example-edit-${ex.example_id}`}
          className="h-7 px-2 text-[color:var(--text-3)] hover:text-[color:var(--text-1)]"
        >
          <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onDelete}
          data-testid={`brand-example-delete-${ex.example_id}`}
          className="h-7 px-2 text-[color:var(--text-3)] hover:text-[color:var(--danger)]"
        >
          <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
        </Button>
      </div>
    </>
  );
}

// ------------------------------------------------------------------
// AddSingleForm with 3 input modes: Paste | Upload | URL
// ------------------------------------------------------------------
const INPUT_MODES = [
  { id: "paste", label: "Paste caption", icon: TypeIcon },
  { id: "upload", label: "Upload screenshot", icon: ImageIcon },
  { id: "url", label: "Image URL", icon: Link2 },
];

function AddSingleForm({ meta, saving, onSubmit, onCancel, initialMode = "paste" }) {
  const [mode, setMode] = useState(initialMode);
  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);
  const [form, setForm] = useState({
    text: "",
    platform: "",
    date: "",
    notes: "",
    tags: [],
    image_url: "",
    is_high_performer: false,
  });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const toggleTag = (t) =>
    setForm((s) => ({
      ...s,
      tags: s.tags.includes(t) ? s.tags.filter((x) => x !== t) : [...s.tags, t],
    }));

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await compressImageFile(file);
      setForm((s) => ({ ...s, image_url: dataUrl }));
      toast.success("Screenshot ready");
    } catch (e) {
      toast.error(e.message || "Couldn't process that image");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clearImage = () =>
    setForm((s) => ({ ...s, image_url: "" }));

  const submit = async () => {
    const hasText = (form.text || "").trim().length >= 4;
    const hasImage = !!form.image_url;
    if (!hasText && !hasImage) {
      toast.error("Add caption text, a screenshot, or an image URL.");
      return;
    }
    if (mode === "url" && form.image_url && !/^https?:\/\//i.test(form.image_url)) {
      toast.error("Image URL must start with http:// or https://");
      return;
    }
    const ok = await onSubmit({
      text: form.text.trim() || null,
      platform: form.platform || null,
      date: form.date || null,
      notes: form.notes || null,
      tags: form.tags,
      image_url: form.image_url || null,
      is_high_performer: !!form.is_high_performer,
    });
    if (ok) {
      setForm({
        text: "",
        platform: "",
        date: "",
        notes: "",
        tags: [],
        image_url: "",
        is_high_performer: false,
      });
      setMode("paste");
    }
  };

  return (
    <div className="space-y-3" data-testid="brand-add-form">
      {/* Mode switcher */}
      <div className="grid grid-cols-3 gap-1 p-1 rounded-md bg-[color:var(--surface-2)] border border-[color:var(--border-subtle)]">
        {INPUT_MODES.map((m) => {
          const active = mode === m.id;
          const Icon = m.icon;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => setMode(m.id)}
              data-testid={`brand-add-mode-${m.id}`}
              className={
                "flex items-center justify-center gap-1.5 text-[11px] font-medium px-2 py-1.5 rounded transition " +
                (active
                  ? "bg-[color:var(--surface-1)] text-[color:var(--text-1)] shadow-sm"
                  : "text-[color:var(--text-3)] hover:text-[color:var(--text-1)]")
              }
            >
              <Icon className="h-3.5 w-3.5" /> {m.label}
            </button>
          );
        })}
      </div>

      {/* Mode-specific input */}
      {mode === "upload" ? (
        <div data-testid="brand-add-upload-zone">
          <label className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]">
            Screenshot or downloaded post image
          </label>
          {form.image_url ? (
            <div className="mt-1.5 relative">
              <img
                src={form.image_url}
                alt="Upload preview"
                className="w-full max-h-64 object-contain rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]"
                data-testid="brand-add-image-preview"
              />
              <button
                type="button"
                onClick={clearImage}
                data-testid="brand-add-image-clear"
                className="absolute top-2 right-2 inline-flex items-center justify-center h-7 w-7 rounded-full bg-[color:rgba(0,0,0,0.55)] text-white hover:bg-[color:rgba(0,0,0,0.75)]"
                aria-label="Remove image"
              >
                <XIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <label
              htmlFor="brand-add-file-input"
              className="mt-1.5 flex flex-col items-center justify-center gap-2 cursor-pointer rounded-md border border-dashed border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] hover:bg-[color:var(--surface-3)] py-8 px-4 text-center transition"
              data-testid="brand-add-file-dropzone"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin text-[color:var(--text-3)]" />
                  <span className="text-xs text-[color:var(--text-3)]">Compressing…</span>
                </>
              ) : (
                <>
                  <Upload className="h-5 w-5 text-[color:var(--text-3)]" />
                  <span className="text-xs text-[color:var(--text-2)]">
                    Click to select a JPG, PNG, or WEBP
                  </span>
                  <span className="text-[10px] text-[color:var(--text-3)]">
                    Max 8 MB · auto-compressed for storage
                  </span>
                </>
              )}
              <input
                ref={fileRef}
                id="brand-add-file-input"
                data-testid="brand-add-file-input"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => handleFile(e.target.files?.[0])}
                disabled={uploading}
                className="hidden"
              />
            </label>
          )}
          <div className="mt-3">
            <label className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]">
              Caption text from this post
            </label>
            <Textarea
              value={form.text}
              onChange={(e) => setForm((s) => ({ ...s, text: e.target.value }))}
              placeholder="Type or paste the caption from this screenshot so Postmoro can learn from it."
              rows={4}
              maxLength={meta?.text_max_length || 4000}
              data-testid="brand-add-upload-text"
              className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
            />
            <div className="text-[11px] text-[color:var(--text-3)] mt-1">
              You can save the screenshot without a caption now and add the caption later — but it won't count toward analysis until caption text is added.
            </div>
          </div>
        </div>
      ) : mode === "url" ? (
        <div data-testid="brand-add-url-zone">
          <label className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]">
            Public image URL
          </label>
          <Input
            value={form.image_url}
            onChange={(e) => setForm((s) => ({ ...s, image_url: e.target.value }))}
            placeholder="https://…/post.jpg"
            data-testid="brand-add-image-url"
            className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
          />
          {form.image_url && /^https?:\/\//i.test(form.image_url) ? (
            <div className="mt-2">
              <img
                src={form.image_url}
                alt="URL preview"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
                className="w-full max-h-48 object-contain rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]"
              />
            </div>
          ) : null}
          <div className="mt-3">
            <label className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]">
              Caption text from this post
            </label>
            <Textarea
              value={form.text}
              onChange={(e) => setForm((s) => ({ ...s, text: e.target.value }))}
              placeholder="Type or paste the caption from this post."
              rows={4}
              maxLength={meta?.text_max_length || 4000}
              data-testid="brand-add-url-text"
              className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
            />
          </div>
        </div>
      ) : (
        <div data-testid="brand-add-paste-zone">
          <label className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]">
            Post caption / text
          </label>
          <Textarea
            value={form.text}
            onChange={(e) => setForm((s) => ({ ...s, text: e.target.value }))}
            placeholder="Paste the full caption from your past post here…"
            rows={6}
            maxLength={meta?.text_max_length || 4000}
            data-testid="brand-add-text"
            className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
          />
        </div>
      )}

      {/* Common fields below mode switcher */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]">Platform (optional)</label>
          <Select
            value={form.platform || "__none__"}
            onValueChange={(v) =>
              setForm((s) => ({ ...s, platform: v === "__none__" ? "" : v }))
            }
          >
            <SelectTrigger
              className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
              data-testid="brand-add-platform"
            >
              <SelectValue placeholder="Pick one" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Not sure / skip</SelectItem>
              {(meta?.platforms || []).map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]">Date (optional)</label>
          <Input
            value={form.date}
            onChange={(e) => setForm((s) => ({ ...s, date: e.target.value }))}
            placeholder="e.g. Aug 2024"
            data-testid="brand-add-date"
            maxLength={40}
            className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
          />
        </div>
      </div>

      <div>
        <label className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]">Notes (optional)</label>
        <Input
          value={form.notes}
          onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
          placeholder='e.g. "Got the most comments this month"'
          data-testid="brand-add-notes"
          maxLength={400}
          className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
        />
      </div>

      <div className="flex items-start gap-3 rounded-md bg-[color:var(--surface-2)] border border-[color:var(--border-subtle)] px-3 py-2.5">
        <Switch
          checked={!!form.is_high_performer}
          onCheckedChange={(v) => setForm((s) => ({ ...s, is_high_performer: v }))}
          data-testid="brand-add-performed-toggle"
          className="mt-0.5"
        />
        <div>
          <div className="text-xs text-[color:var(--text-1)] font-medium flex items-center gap-1.5">
            <Star className="h-3.5 w-3.5 text-[color:var(--warning)]" />
            This post performed well
          </div>
          <div className="text-[11px] text-[color:var(--text-3)]">
            Postmoro will weight high-performers more heavily when learning your voice.
          </div>
        </div>
      </div>

      <div>
        <label className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]">Tags (optional)</label>
        <div className="mt-2 flex flex-wrap gap-2">
          {(meta?.tags || []).map((t) => {
            const active = form.tags.includes(t);
            return (
              <button
                key={t}
                type="button"
                onClick={() => toggleTag(t)}
                data-testid={`brand-add-tag-${t.replace(/[^a-z]/gi, "")}`}
                className={
                  "text-[11px] px-2 py-1 rounded-full border transition " +
                  (active
                    ? "bg-[color:rgba(27,168,159,0.12)] border-[color:var(--brand-primary)] text-[color:var(--brand-primary)]"
                    : "bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-2)] hover:text-[color:var(--text-1)]")
                }
              >
                {active ? "✓ " : ""}{t}
              </button>
            );
          })}
        </div>
      </div>

      <DialogFooter className="gap-2">
        <Button variant="ghost" onClick={onCancel} data-testid="brand-add-cancel">Cancel</Button>
        <Button
          onClick={submit}
          disabled={saving || uploading}
          data-testid="brand-add-submit"
          className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)]"
        >
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />}
          Add example
        </Button>
      </DialogFooter>
    </div>
  );
}

function BulkPasteForm({ meta, saving, onSubmit, onCancel, remainingCapacity }) {
  const [text, setText] = useState("");
  const [platform, setPlatform] = useState("");
  return (
    <div className="space-y-3" data-testid="brand-bulk-form">
      <div className="text-xs text-[color:var(--text-2)]">
        Paste your past post captions below. Separate each post with a{" "}
        <strong>blank line</strong>. Up to <strong>{remainingCapacity}</strong> can be added right now.
      </div>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={10}
        placeholder={"Post 1 caption here.\n\nPost 2 caption here.\n\nPost 3 caption here."}
        data-testid="brand-bulk-text"
        className="bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] font-mono text-xs"
      />
      <div>
        <label className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]">Default platform (optional, applied to all)</label>
        <Select value={platform || "__none__"} onValueChange={(v) => setPlatform(v === "__none__" ? "" : v)}>
          <SelectTrigger className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]" data-testid="brand-bulk-platform">
            <SelectValue placeholder="Pick one" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Mixed / skip</SelectItem>
            {(meta?.platforms || []).map((p) => (
              <SelectItem key={p} value={p}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <DialogFooter className="gap-2">
        <Button variant="ghost" onClick={onCancel} data-testid="brand-bulk-cancel">Cancel</Button>
        <Button
          onClick={() => onSubmit(text, platform)}
          disabled={saving || !text.trim()}
          data-testid="brand-bulk-submit"
          className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)]"
        >
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
          Import
        </Button>
      </DialogFooter>
    </div>
  );
}

function ExampleEditor({ initial, meta, onCancel, onSave }) {
  const [draft, setDraft] = useState({
    text: initial.text || "",
    platform: initial.platform || "",
    date: initial.date || "",
    notes: initial.notes || "",
    tags: initial.tags || [],
    image_url: initial.image_url || "",
    is_high_performer: !!initial.is_high_performer,
  });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const captionRef = useRef(null);

  // Auto-focus the caption when opening on an image-only example.
  // Driven by the prominent "Add caption text" button on the example row.
  useEffect(() => {
    if (!(initial.text || "").trim() && initial.image_url && captionRef.current) {
      captionRef.current.focus();
      // Scroll into view inside the dialog if needed.
      captionRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
    }
    // Only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleTag = (t) =>
    setDraft((s) => ({
      ...s,
      tags: s.tags.includes(t) ? s.tags.filter((x) => x !== t) : [...s.tags, t],
    }));

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await compressImageFile(file);
      setDraft((s) => ({ ...s, image_url: dataUrl }));
    } catch (e) {
      toast.error(e.message || "Couldn't process that image");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-3" data-testid={`brand-example-editor-${initial.example_id}`}>
      {draft.image_url ? (
        <div className="relative">
          <img
            src={draft.image_url}
            alt=""
            className="w-full max-h-48 object-contain rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-3)]"
          />
          <button
            type="button"
            onClick={() => setDraft((s) => ({ ...s, image_url: "" }))}
            className="absolute top-2 right-2 inline-flex items-center justify-center h-7 w-7 rounded-full bg-[color:rgba(0,0,0,0.55)] text-white hover:bg-[color:rgba(0,0,0,0.75)]"
            aria-label="Remove image"
          >
            <XIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <label
          htmlFor={`brand-edit-file-${initial.example_id}`}
          className="flex items-center justify-center gap-2 cursor-pointer rounded-md border border-dashed border-[color:var(--border-subtle)] bg-[color:var(--surface-3)] py-3 px-3 text-[11px] text-[color:var(--text-3)] hover:bg-[color:var(--surface-2)] transition"
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ImageIcon className="h-4 w-4" />
          )}
          Add screenshot (optional)
          <input
            ref={fileRef}
            id={`brand-edit-file-${initial.example_id}`}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => handleFile(e.target.files?.[0])}
            disabled={uploading}
            className="hidden"
          />
        </label>
      )}
      {!(draft.text || "").trim() && draft.image_url ? (
        <div className="text-[11px] text-[color:var(--text-2)] -mb-1.5 leading-snug">
          <span className="inline-flex items-center gap-1 text-[color:var(--warning)] font-medium">
            <AlertTriangle className="h-3 w-3" /> Caption needed
          </span>{" "}
          Type or paste the caption from this screenshot so Postmoro can study its writing style.
        </div>
      ) : null}
      <Textarea
        ref={captionRef}
        value={draft.text}
        onChange={(e) => setDraft((s) => ({ ...s, text: e.target.value }))}
        rows={4}
        placeholder="Caption text from this post"
        className="bg-[color:var(--surface-3)] border-[color:var(--border-subtle)]"
      />
      <div className="grid grid-cols-2 gap-2">
        <Select
          value={draft.platform || "__none__"}
          onValueChange={(v) =>
            setDraft((s) => ({ ...s, platform: v === "__none__" ? "" : v }))
          }
        >
          <SelectTrigger className="h-9 bg-[color:var(--surface-3)] border-[color:var(--border-subtle)] text-xs">
            <SelectValue placeholder="Platform" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">No platform</SelectItem>
            {(meta?.platforms || []).map((p) => (
              <SelectItem key={p} value={p}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={draft.date}
          onChange={(e) => setDraft((s) => ({ ...s, date: e.target.value }))}
          placeholder="Date (optional)"
          className="h-9 bg-[color:var(--surface-3)] border-[color:var(--border-subtle)] text-xs"
        />
      </div>
      <Input
        value={draft.notes}
        onChange={(e) => setDraft((s) => ({ ...s, notes: e.target.value }))}
        placeholder="Notes (optional)"
        className="h-9 bg-[color:var(--surface-3)] border-[color:var(--border-subtle)] text-xs"
      />
      <div className="flex items-center gap-2">
        <Switch
          checked={!!draft.is_high_performer}
          onCheckedChange={(v) => setDraft((s) => ({ ...s, is_high_performer: v }))}
        />
        <span className="text-[11px] text-[color:var(--text-2)] inline-flex items-center gap-1">
          <Star className="h-3 w-3 text-[color:var(--warning)]" /> Performed well
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {(meta?.tags || []).map((t) => {
          const active = draft.tags.includes(t);
          return (
            <button
              key={t}
              type="button"
              onClick={() => toggleTag(t)}
              className={
                "text-[10px] px-2 py-0.5 rounded-full border transition " +
                (active
                  ? "bg-[color:rgba(27,168,159,0.12)] border-[color:var(--brand-primary)] text-[color:var(--brand-primary)]"
                  : "bg-[color:var(--surface-3)] border-[color:var(--border-subtle)] text-[color:var(--text-3)] hover:text-[color:var(--text-1)]")
              }
            >
              {active ? "✓ " : ""}{t}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2 justify-end">
        <Button size="sm" variant="ghost" onClick={onCancel} className="h-8 px-2">
          <XIcon className="h-3.5 w-3.5 mr-1" /> Cancel
        </Button>
        <Button
          size="sm"
          onClick={() =>
            onSave({
              text: draft.text,
              platform: draft.platform || null,
              date: draft.date || null,
              notes: draft.notes || null,
              tags: draft.tags,
              image_url: draft.image_url || "",
              is_high_performer: !!draft.is_high_performer,
            })
          }
          className="h-8 px-2 bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)]"
        >
          <Save className="h-3.5 w-3.5 mr-1" /> Save
        </Button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// SocialImportCard — truthful per-platform status for future automatic
// brand-learning import. Manual examples work today. Auto-import will
// land once each platform's official API is verified for read access.
//
// Per-platform copy rules (avoid the prior "Coming soon" confusion):
//   * X / Twitter:
//       - if connected: "Connected — auto-import not available yet"
//       - if not connected: "Not connected"
//   * Instagram / Facebook: "Requires Meta approval" (gated by Meta App Review)
//   * LinkedIn:             "Planned" (integration arrives after launch)
// ------------------------------------------------------------------
function SocialImportCard() {
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/connections");
        if (cancelled) return;
        setPlatforms(res.data?.platforms || []);
      } catch {
        if (!cancelled) setPlatforms([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // For each platform: pick the right label + tone based on
  // (a) whether the platform integration is even built, and
  // (b) whether the user actually has it connected.
  const statusFor = (p) => {
    const id = (p.platform || "").toLowerCase();
    const isConnected = !!p.connected;
    if (id === "instagram" || id === "facebook") {
      return { label: "Requires Meta approval", tone: "neutral" };
    }
    if (id === "linkedin") {
      return { label: "Planned", tone: "neutral" };
    }
    if (id === "tiktok") {
      // TikTok is hidden from MVP; this branch is a safety net if backend
      // ever surfaces it again.
      return { label: "Not available", tone: "neutral" };
    }
    if (id === "x" || id === "twitter") {
      return isConnected
        ? {
            label: "Connected — automatic import not available yet",
            tone: "ok-muted",
          }
        : { label: "Not connected", tone: "neutral" };
    }
    return isConnected
      ? { label: "Connected", tone: "ok" }
      : { label: "Not connected", tone: "neutral" };
  };

  const renderRow = (p) => {
    const { label: statusLabel, tone } = statusFor(p);
    const toneClass =
      tone === "ok"
        ? "text-[color:var(--brand-primary)]"
        : tone === "ok-muted"
        ? "text-[color:var(--brand-primary)]"
        : "text-[color:var(--text-3)]";
    // Duplicate-label guard: for platforms whose statusLabel already
    // contains the word "Connected" (currently X/Twitter → "Connected —
    // auto-import not available yet"), skip the extra "Connected" pill
    // to avoid the confusing "Connected · Connected — …" double-label.
    // The label itself already carries the connected state.
    const showConnectedPill = tone === "ok";
    return (
      <div
        key={p.platform}
        className="flex items-center justify-between gap-3 py-2 border-b border-[color:var(--border-subtle)] last:border-b-0"
        data-testid={`brand-import-row-${(p.platform || "").toLowerCase()}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm text-[color:var(--text-1)] truncate">
            {p.label || p.platform}
          </span>
          {showConnectedPill ? (
            <span
              className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[color:rgba(27,168,159,0.10)] border border-[color:rgba(27,168,159,0.30)] text-[color:var(--brand-primary)]"
              data-testid={`brand-import-row-${(p.platform || "").toLowerCase()}-connected`}
            >
              <CheckCircle2 className="h-3 w-3" /> Connected
            </span>
          ) : null}
        </div>
        <span className={"text-[11px] text-right shrink-0 ml-2 " + toneClass}>
          {statusLabel}
        </span>
      </div>
    );
  };

  return (
    <GlassCard className="p-6" data-testid="brand-social-import-card">
      <div className="mb-3">
        <div className="eyebrow">Future automation</div>
        <div className="font-display text-lg font-semibold">
          Automatic import from social accounts
        </div>
        <div className="text-xs text-[color:var(--text-3)] mt-1">
          Manual examples work today. Automatic import from your connected
          accounts will be added as each platform&apos;s official API is verified.
        </div>
      </div>
      {loading ? (
        <div className="text-xs text-[color:var(--text-3)] flex items-center gap-2 py-4 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking connections…
        </div>
      ) : platforms.length === 0 ? (
        <div className="text-xs text-[color:var(--text-3)] py-4 text-center">
          No platforms reported. Connect accounts from{" "}
          <a className="text-[color:var(--brand-primary)] hover:underline" href="/settings/connections">
            Connections
          </a>
          .
        </div>
      ) : (
        <div data-testid="brand-import-platforms">{platforms.map(renderRow)}</div>
      )}
    </GlassCard>
  );
}


function StyleProfileView({ profile }) {
  const Row = ({ label, value, testid }) => {
    if (!value || (Array.isArray(value) && value.length === 0)) return null;
    return (
      <div data-testid={testid}>
        <div className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)] mb-1">{label}</div>
        {Array.isArray(value) ? (
          <div className="flex flex-wrap gap-1.5">
            {value.map((v, i) => (
              <span
                key={i}
                className="text-[11px] px-2 py-0.5 rounded-md bg-[color:var(--surface-2)] border border-[color:var(--border-subtle)] text-[color:var(--text-2)]"
              >
                {v}
              </span>
            ))}
          </div>
        ) : (
          <div className="text-sm text-[color:var(--text-1)] leading-relaxed">{value}</div>
        )}
      </div>
    );
  };
  return (
    <div className="space-y-3.5" data-testid="brand-style-view">
      <Row label="Brand voice" value={profile.brand_voice} testid="brand-style-view-voice" />
      <Row label="Tone signature" value={profile.tone} testid="brand-style-view-tone" />
      <Row label="Typical post length" value={profile.typical_length} testid="brand-style-view-length" />
      <Row label="Emoji style" value={profile.emoji_style} testid="brand-style-view-emoji" />
      <Row label="Hashtag style" value={profile.hashtag_style} testid="brand-style-view-hashtag" />
      <Row label="CTA style" value={profile.cta_style} testid="brand-style-view-cta" />
      <Row label="Common phrases" value={profile.common_phrases} testid="brand-style-view-phrases" />
      <Row label="Common topics" value={profile.common_topics} testid="brand-style-view-topics" />
      <Row label="Content themes" value={profile.content_themes} testid="brand-style-view-themes" />
      <Row label="Audience style" value={profile.audience_style} testid="brand-style-view-audience" />
      <Row label="DO guidelines" value={profile.do_guidelines} testid="brand-style-view-dos" />
      <Row label="DON'T guidelines" value={profile.dont_guidelines} testid="brand-style-view-donts" />
      {profile.source_example_count ? (
        <div className="text-[11px] text-[color:var(--text-3)] pt-2 border-t border-[color:var(--border-subtle)]">
          Built from {profile.source_example_count} example
          {profile.source_example_count === 1 ? "" : "s"}.
          {profile.last_analyzed_at
            ? ` Last analyzed ${new Date(profile.last_analyzed_at).toLocaleDateString()}.`
            : ""}
        </div>
      ) : null}
    </div>
  );
}

function StyleProfileEditor({ draft, onChange }) {
  if (!draft) return null;
  const set = (k, v) => onChange({ ...draft, [k]: v });
  const Field = ({ k, label, placeholder, rows = 2 }) => (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]">{label}</label>
      <Textarea
        value={draft[k] || ""}
        onChange={(e) => set(k, e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-sm"
      />
    </div>
  );
  return (
    <div className="space-y-3" data-testid="brand-style-editor">
      <Field k="brand_voice" label="Brand voice" placeholder="Friendly, neighborly, occasionally promotional." rows={2} />
      <Field k="tone" label="Tone signature" placeholder="Friendly, local, upbeat" rows={1} />
      <Field k="typical_length" label="Typical post length" placeholder="Short, 1–2 sentences" rows={1} />
      <Field k="emoji_style" label="Emoji style" placeholder="Uses 1–2 emojis per post, mostly hearts/coffee/seasonal" rows={2} />
      <Field k="hashtag_style" label="Hashtag style" placeholder="Uses local geo + service hashtags" rows={2} />
      <Field k="cta_style" label="CTA style" placeholder="Soft conversational invitations like 'Come grab one'" rows={2} />
      <Field k="common_phrases" label="Common phrases (comma-separated)" placeholder="Shout out to, Real talk, Sunday vibes" rows={2} />
      <Field k="common_topics" label="Common topics (comma-separated)" placeholder="Cold brew, scones, regulars" rows={2} />
      <Field k="content_themes" label="Content themes (comma-separated)" placeholder="Community, seasonal menu, cozy moments" rows={2} />
      <Field k="audience_style" label="Audience style" placeholder="Local Austin regulars who love a cozy cafe" rows={2} />
      <Field k="do_guidelines" label="DO guidelines (one per line)" placeholder={"Keep captions short and punchy\nReference specific days or seasons"} rows={3} />
      <Field k="dont_guidelines" label="DON'T guidelines (one per line)" placeholder={"Avoid long multi-paragraph posts\nAvoid corporate marketing-speak"} rows={3} />
    </div>
  );
}

